import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import logger from '../../utils/logger';
import { platformStorage } from '../../utils/storage';
import { api } from './client';

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  verify2FA: async (
    tempToken: string,
    code: string,
    method: 'totp' | 'email_otp' | 'backup_code',
  ) => {
    const payload = {
      tempToken,
      code,
      method,
    };

    logger.log('[verify2FA] 🔐 Sending verification request:', {
      url: `${API_BASE_URL}/auth/verify-2fa`,
      httpMethod: 'POST',
      verificationMethod: method,
      codeLength: code?.length,
      hasTempToken: !!tempToken,
    });

    try {
      const tempApi = axios.create({
        baseURL: API_BASE_URL,
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tempToken}`,
        },
      });

      const response = await tempApi.post('/auth/verify-2fa', payload);

      logger.log('[verify2FA] ✅ Verification successful:', {
        status: response.status,
        hasTokens: !!response.data?.tokens,
        hasUser: !!response.data?.user,
      });

      return response.data;
    } catch (error: any) {
      logger.error('[verify2FA] ❌ Verification failed:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        errorData: error?.response?.data,
        errorMessage: error?.message,
        url: error?.config?.url,
        method: error?.config?.method,
      });

      const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(backendMessage || error?.message || 'Verification failed');
    }
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refresh: async (refreshToken: string) => {
    const response = await api.post('/auth/refresh', {
      refreshToken: refreshToken,
    });
    return response.data;
  },

  registerPushToken: async (token: string) => {
    const response = await api.patch('/users/me/push-token', { token });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.patch('/users/me/password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  updateProfile: async (data: { firstName?: string; lastName?: string }) => {
    const response = await api.patch('/users/me', data);
    return response.data;
  },
};
